import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyRound, Trash2, UserPlus } from "lucide-react";

const ROLE_OPTS = [
  { value: "admin", label: "Administrador" },
  { value: "operator_mayor", label: "Operador Mayor" },
  { value: "operator_detalle", label: "Operador Detalle" },
  { value: "readonly", label: "Solo Lectura" },
];

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operator_detalle" });
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [deleteUser, setDeleteUser] = useState(null);

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("Usuario creado"); setOpen(false); setForm({ name: "", email: "", password: "", role: "operator_detalle" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleStatus = async (u) => {
    try { await api.put(`/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); toast.success("Rol actualizado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openReset = (u) => { setResetUser(u); setResetPassword(""); };
  const resetUserPassword = async () => {
    if (!resetUser || !resetPassword.trim()) {
      toast.error("Escribe la nueva contrasena");
      return;
    }
    try {
      await api.put(`/users/${resetUser.id}`, { password: resetPassword });
      toast.success("Contrasena restablecida");
      setResetUser(null);
      setResetPassword("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/users/${deleteUser.id}`);
      toast.success("Usuario eliminado");
      setDeleteUser(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Usuarios</h1>
          <p className="text-slate-500 mt-1">Gestión de usuarios y roles por mercado</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-user-btn"><UserPlus size={16} className="mr-2" /> Nuevo Usuario</Button>
      </div>

      <Card className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Nombre</th>
              <th className="px-4 py-3 text-left font-semibold">Email</th>
              <th className="px-4 py-3 text-left font-semibold">Rol</th>
              <th className="px-4 py-3 text-left font-semibold">Estado</th>
              <th className="px-4 py-3 text-right font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_OPTS.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <Badge className={u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{u.status}</Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openReset(u)} data-testid={`reset-user-${u.id}`}>
                      <KeyRound size={14} className="mr-1.5" /> Reset
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(u)} data-testid={`toggle-user-${u.id}`}>{u.status === "active" ? "Desactivar" : "Activar"}</Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.id}`} className="text-rose-600 hover:text-rose-700">
                      <Trash2 size={14} className="mr-1.5" /> Eliminar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader><DialogTitle>Crear Usuario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><Label>Contraseña</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTS.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} className="bg-slate-900" data-testid="create-user-confirm">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) setResetUser(null); }}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Restablecer contrasena</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Usuario: <span className="font-semibold text-slate-900">{resetUser?.name}</span>
            </div>
            <div>
              <Label>Nueva contrasena</Label>
              <Input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} data-testid="reset-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)}>Cancelar</Button>
            <Button onClick={resetUserPassword} className="bg-slate-900" data-testid="reset-password-confirm">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteUser} onOpenChange={(o) => { if (!o) setDeleteUser(null); }}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Eliminar usuario</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">
            Se eliminara el usuario <span className="font-semibold text-slate-900">{deleteUser?.email}</span>. Esta accion no se puede deshacer.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancelar</Button>
            <Button onClick={confirmDeleteUser} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-user-confirm">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
